import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {REMIT_BASE_URL} from './config';
import {RemitApiResponse} from './kyc/user-verification.service';
import {Fee} from './order-fees.service';
import {AgentFee} from './remit-agency-partner.service';
import {
  PaginationParams,
  Promo,
  RemitInvoice,
  RemitReceiptMessage,
} from './remit-invoice.service';

export interface Pay88MemberCheckRequest {
  arcNumber: string;
}

export interface Pay88MemberCheckResult {
  isPay88Member: boolean;
  isVerifiedByAgent: boolean;
  memberId?: string;
}

export interface Pay88MemberCheckResponse {
  message: string;
  body: Pay88MemberCheckResult;
}

export interface AgentAccountProfile {
  id: string;
  externalUserId: string;
  agentId: string;
  createdAt: string;
  createdBy: string;
  version: string;
  agentName: string;
  username: string;
}

export interface AgentAccountProfileResponse {
  message: string;
  body: AgentAccountProfile;
}

export interface AgentFeeLatestResponse {
  message: string;
  body: AgentFee;
}

export interface VerifyPay88MemberRequest {
  arcNumber: string;
  name: string;
  dateOfBirth: string;
  arcExpiredDate: string;
}

export interface VerifiedPay88Member {
  memberId: string;
  name: string;
  arcNumber: string;
  passportNumber: string;
  arcExpiredDate: string;
  phoneNumber: string;
  arcFrontImageUrl: string;
  arcBackImageUrl: string;
  selfieImageUrl: string;
  signatureImageUrl: string;
}

export interface VerifyPay88MemberResponse {
  message: string;
  body: VerifiedPay88Member;
}

export interface CreatePay88MemberRequest {
  name: string;
  arcNo: string;
  arcExpiredDate: string;
  dateOfBirth: string;
  passportNumber: string;
  arcFrontImage: string;
  arcBackImage: string;
  selfieImage: string;
  userSignatureImage: string;
  phoneNumber: string;
}

export interface CreatePay88MemberResult {
  memberId: string;
}

export interface CreatePay88MemberResponse {
  message: string;
  body: CreatePay88MemberResult;
}

export interface CreateBeneficiaryRequest {
  bankCode: string;
  accountNumber: string;
  dateOfBirth?: string;
}

export interface BeneficiaryAccount {
  id: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  status: string;
  lastInquiryAt: string;
  userId: string;
  createdAt: string;
  createdBy: string;
  isDeleted: 'Y' | 'N';
  updatedAt: string;
  updatedBy: string;
  inquiryMethod: string;
  dateOfBirth?: string;
  disabled: boolean;
  reason?: string;
}

export interface BeneficiaryAccountsResponse {
  message: string;
  body: BeneficiaryAccount[];
  totalData: number;
}

export interface AgentVerifiedMembersQueryParams extends PaginationParams {
  name?: string;
  phoneNumber?: string;
  arcNumber?: string;
  referralCode?: string;
  agencyReferralCode?: string;
  agencyId?: string;
  registrationSource?: string;
  excludedRegistrationSources?: string[];
  sortBy?: string;
  sortDirection?: number;
  memberId?: string;
  memberIds?: string[];
}

export interface AgentVerifiedMember {
  id: string;
  arcNo?: string;
  name: string;
  phoneNumber?: string;
  passportNumber?: string;
  registrationSource?: string;
  agencyId?: string;
  isMemberByAgentVerified?: boolean;
  createdAt?: string;
  createdBy?: string;
  arcExpiredDate?: string;
  arcFrontImageUrl?: string;
  arcBackImageUrl?: string;
  selfieImageUrl?: string;
  signatureImageUrl?: string;
  arcFrontImage?: string;
  arcBackImage?: string;
  selfieImage?: string;
  userSignatureImage?: string;
}

export interface AgentVerifiedMembersResponse {
  message: string;
  body: AgentVerifiedMember[];
  totalData: number;
}

export type RemitCalculationFrom = 'source' | 'target';
export type RemitUsePoint = 'Y' | 'N';
export type RemitPaymentMode = 0 | 1;
export type RemitCreationSource = 0 | 1;
export type RemitEinvoiceCarrierType = 0 | 1;

export type RemitPurposeOfRemittance =
  | 'PERSONAL_TRANSFER'
  | 'FAMILY_SUPPORT'
  | 'GIFT_AND_DONATION'
  | 'MEDICAL_TREATMENT'
  | 'TRAVEL'
  | 'EDUCATION'
  | 'UTILITY_BILLS'
  | 'LOAN_PAYMENT'
  | 'PROPERTY_PURCHASE'
  | 'OTHER';

export interface AgentRemitParams {
  calculateFrom: RemitCalculationFrom;
  amountNTD?: string;
  foreignAmount?: string;
  foreignCurrency?: string;
  usePoint?: RemitUsePoint;
  beneficiaryId?: string;
  longitude?: number;
  latitude?: number;
  purposeOfRemittance?: RemitPurposeOfRemittance;
  voucherId?: string;
  amountFee?: string;
  amountDiscount?: string;
  shopCode?: string;
  userId: string;
  paymentMode?: RemitPaymentMode;
  note?: string;
  creationSource?: RemitCreationSource;
  relateId?: string;
  agencyId?: string;
  einvoiceCarrierType?: RemitEinvoiceCarrierType;
  einvoiceCarrierNumber?: string;
}

export interface AgentPrecheckoutResult {
  amountNTD: string;
  foreignCurrency: string;
  foreignAmount: string;
  rate: string;
  usePoint: RemitUsePoint;
  totalPaymentNTD: string;
  promos: Promo[];
  fees: Fee[];
  canCheckout: 'Y' | 'N';
  errorMessage?: string;
  beneficiaryName: string;
  beneficiaryBankCode: string;
  beneficiaryAccountNumber: string;
  beneficiaryInquiryMethod: string;
  beneficiaryDateOfBirth: string;
  purposeOfRemittance?: RemitPurposeOfRemittance;
  beneficiaryId: string;
  shopCode?: string;
  note?: string;
  einvoiceCarrierType?: RemitEinvoiceCarrierType;
  einvoiceCarrierNumber?: string;
}

export interface AgentPrecheckoutResponse {
  message: string;
  body: AgentPrecheckoutResult;
}

export interface AgentCheckoutResponse {
  message: string;
  id: string;
}

export interface AgentRemitInvoice extends RemitInvoice {
  attachments?: string[];
  arcExpiredDate?: string;
  purposeOfRemittance?: string;
  version?: string;
  ipAddress?: string;
  latitude?: number;
  longitude?: number;
  paymentMethod?: string;
  cancelAt?: string;
  cancelBy?: string;
  isManualTransfer?: string;
  refundedAt?: string;
  refundedBy?: string;
  bankReportRenderedAt?: string;
  bankReportRenderedBy?: string;
  aggregatedValue?: string;
  beneficiaryId?: string;
  einvoiceTime?: string;
  einvoiceBy?: string;
  einvoiceCarrierType?: number;
  einvoiceCarrierNumber?: string;
  sendReceiptAt?: string;
  sendReceiptBy?: string;
  loanContractId?: string;
  loanRedirectUrl?: string;
  creationSource?: RemitCreationSource;
  relateId?: string;
  agencyId?: string;
  einvoiceMethodName?: string;
}

export interface AgentRemitInvoiceResponse {
  message: string;
  body: AgentRemitInvoice;
}

export interface AgentRemitInvoicesResponse {
  message: string;
  body: AgentRemitInvoice[];
  totalData: number;
}

export interface AgentRemittanceUploadImageResponse {
  message: string;
  url: string;
}

export interface AgentRemittanceRateResponse {
  message: string;
  rate: string;
}

export type AgentBankCodeVisibility = 'Y' | 'N';

export interface AgentBankCodeQueryParams extends PaginationParams {
  bankCodes?: string[];
  thunesId?: string;
  sortBy?: string;
  sortDirection?: number;
  isHidden?: AgentBankCodeVisibility;
}

export interface AgentBankCode {
  bankCode: string;
  bankName: string;
  legalBankName: string;
  thunesId: string;
  swiftCode: string;
  isHidden: AgentBankCodeVisibility;
}

export interface AgentBankCodesResponse {
  message: string;
  body: AgentBankCode[];
  totalData: number;
}

export type AgentRemitInvoiceQueryParams = PaginationParams & {
  arcNumber?: string;
  phoneNumber?: string;
  name?: string;
  timezone?: string;
  userId?: string;
  userIds?: string[];
  transactionIds?: string[];
  paymentCodes?: string[];
  hasReported?: string;
  hasPaid?: string;
  bankReportValidFor?: string;
  bankReportValidForCurrentTime?: string;
  paidTimeStart?: string;
  paidTimeEnd?: string;
  hasTransferred?: string;
  bankReportId?: string;
  bankReportBankId?: string;
  bankReportIds?: string[];
  status?: string[];
  bankCode?: string;
  excludeBankCode?: string;
  transferMethod?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  hasProblem?: string;
  arcExpiredDate?: string;
  isOnHold?: string;
  einvoiceNo?: string;
  invoicedTimeStart?: string;
  invoicedTimeEnd?: string;
  sortBy?: string;
  sortDirection?: number;
  reportedAtStart?: string;
  reportedAtEnd?: string;
  beneficiaryAccountNumber?: string;
  userStatus?: string;
  userStatuses?: string[];
  purposeOfRemittance?: string;
  transferredAtStart?: string;
  transferredAtEnd?: string;
  submitStatus?: string[];
  hasInvoiced?: string;
  hasProcessed?: string;
  beneficiaryName?: string;
  hasPromos?: string;
  promoIds?: string[];
  hasExpired?: string;
  hasRefunded?: string;
  shopCode?: string;
  shopCodes?: string[];
  excludedShopCodes?: string[];
  paymentMethod?: string;
  einvoiceAwardStatus?: string;
  einvoiceAwardType?: string;
  einvoiceMethod?: string;
  transferRefNumber?: string;
  loanContractId?: string;
  isLoanAgree?: string;
  einvoiceCarrierType?: number;
  einvoiceCarrierNumber?: string;
  creationSource?: RemitCreationSource;
  transferMethods?: string[];
  onHoldAtStart?: string;
  onHoldAtEnd?: string;
  agencyId?: string;
  agencyIds?: string[];
};

@Injectable({
  providedIn: 'root',
})
export class RemitAgentRemittanceService {
  private readonly base: string;

  constructor(
    private http: HttpClient,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) {
    this.base = `${this.remitApiUrl}/agent`;
  }

  getAgentAccount(): Observable<AgentAccountProfileResponse> {
    return this.http.get<AgentAccountProfileResponse>(
        `${this.base}/agent/account`,
    );
  }

  getLatestAgentFee(): Observable<AgentFeeLatestResponse> {
    return this.http.get<AgentFeeLatestResponse>(
        `${this.base}/agent-fee/latest`,
    );
  }

  pay88MemberCheck(
      payload: Pay88MemberCheckRequest,
  ): Observable<Pay88MemberCheckResponse> {
    return this.http.post<Pay88MemberCheckResponse>(
        `${this.base}/remittance/pay88-member-check`,
        this.normalizePay88Payload(payload),
    );
  }

  verifyPay88Member(
      payload: VerifyPay88MemberRequest,
  ): Observable<VerifyPay88MemberResponse> {
    return this.http.post<VerifyPay88MemberResponse>(
        `${this.base}/remittance/verify-pay88-member`,
        this.normalizeVerifyPayload(payload),
    );
  }

  createPay88Member(
      payload: CreatePay88MemberRequest,
  ): Observable<CreatePay88MemberResponse> {
    return this.http.post<CreatePay88MemberResponse>(
        `${this.base}/agent/member`,
        this.normalizeCreatePay88MemberPayload(payload),
    );
  }

  createBeneficiary(
      memberId: string,
      payload: CreateBeneficiaryRequest,
  ): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        `${this.base}/member/${memberId}/beneficiary`,
        this.normalizeBeneficiaryPayload(payload),
    );
  }

  queryBeneficiaries(
      memberId: string,
      params?: PaginationParams,
  ): Observable<BeneficiaryAccountsResponse> {
    return this.http.post<BeneficiaryAccountsResponse>(
        `${this.base}/member/${memberId}/beneficiaries`,
        params ?? {},
    );
  }

  queryVerifiedMembers(
      params?: AgentVerifiedMembersQueryParams,
  ): Observable<AgentVerifiedMembersResponse> {
    return this.http.post<AgentVerifiedMembersResponse>(
        `${this.base}/verified-members`,
        params ?? {},
    );
  }

  precheckout(
      payload: AgentRemitParams,
  ): Observable<AgentPrecheckoutResponse> {
    return this.http.post<AgentPrecheckoutResponse>(
        `${this.base}/precheckout`,
        payload,
    );
  }

  checkout(
      payload: AgentRemitParams,
  ): Observable<AgentCheckoutResponse> {
    return this.http.post<AgentCheckoutResponse>(
        `${this.base}/checkout`,
        payload,
    );
  }

  getRemitInvoice(id: string): Observable<AgentRemitInvoiceResponse> {
    return this.http.get<AgentRemitInvoiceResponse>(
        `${this.base}/remit-invoice/${id}`,
    );
  }

  cancelTransaction(id: string): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        `${this.base}/remit-invoice/${id}/cancel`,
        {},
    );
  }

  changeTransactionAmount(
      id: string,
      amountNTD: string,
  ): Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        `${this.base}/remit-invoice/${id}/change-amount`,
        {amountNTD},
    );
  }

  getReceiptMessage(id: string): Observable<RemitReceiptMessage> {
    return this.http.get<RemitReceiptMessage>(
        `${this.base}/remit-invoice/${id}/receipt-message`,
    );
  }

  queryRemitInvoices(
      params: AgentRemitInvoiceQueryParams,
  ): Observable<AgentRemitInvoicesResponse> {
    return this.http.post<AgentRemitInvoicesResponse>(
        `${this.base}/remit-invoices`,
        params ?? {},
    );
  }

  uploadRemittanceImage(file: File):
    Observable<AgentRemittanceUploadImageResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<AgentRemittanceUploadImageResponse>(
        `${this.base}/remittance/upload-image`,
        formData,
    );
  }

  getRemittanceRate(): Observable<AgentRemittanceRateResponse> {
    return this.http.get<AgentRemittanceRateResponse>(
        `${this.base}/rate`,
    );
  }

  queryBankCodes(
      params?: AgentBankCodeQueryParams,
  ): Observable<AgentBankCodesResponse> {
    return this.http.post<AgentBankCodesResponse>(
        `${this.base}/bankcodes`,
        params ?? {},
    );
  }

  private normalizePay88Payload(
      payload: Pay88MemberCheckRequest,
  ): Pay88MemberCheckRequest {
    return {arcNumber: this.normalizeArcNumber(payload.arcNumber)};
  }

  private normalizeVerifyPayload(
      payload: VerifyPay88MemberRequest,
  ): VerifyPay88MemberRequest {
    return {
      ...payload,
      arcNumber: this.normalizeArcNumber(payload.arcNumber),
    };
  }

  private normalizeCreatePay88MemberPayload(
      payload: CreatePay88MemberRequest,
  ): CreatePay88MemberRequest {
    return {
      ...payload,
      arcNo: this.normalizeArcNumber(payload.arcNo),
    };
  }

  private normalizeBeneficiaryPayload(
      payload: CreateBeneficiaryRequest,
  ): CreateBeneficiaryRequest {
    const accountNumber = payload.accountNumber.replace(/[\s-]/g, '');
    return {...payload, accountNumber};
  }

  private normalizeArcNumber(arcNumber: string): string {
    return arcNumber.trim().toUpperCase();
  }
}
